export interface FAQItem {
  question: string;
  answer: string;
}

export interface ArticleSection {
  id?: string;
  title: string;
  content?: string;
  list?: string[];
  type?: 'default' | 'accordion' | 'tabs' | 'myth-fact' | 'checklist' | 'buying-steps' | 'calculator' | 'red-flags' | 'comparison-table' | 'seller-questions' | 'quiz' | 'matrix' | 'scenarios' | 'summary';
  items?: any[]; // for accordion, tabs, red-flags, matrix, etc.
  headers?: {
    criteria: string;
    col1: string;
    col2: string;
    col3?: string;
  };
  questions?: any[];
  results?: any;
}

export interface ArticleContent {
  title: string;
  subtitle: string;
  seoTitle?: string;
  seoDescription?: string;
  lead: string;
  sections: ArticleSection[];
  faq: FAQItem[];
  sources?: { name: string; year?: string; organization?: string; url?: string }[];
  takeaways: string[];
}

export interface UsefulArticle {
  id: string;
  slug: string;
  image: string;
  relatedArticles?: string[]; // slugs of related articles in the same hub
  stats?: { label: string; value: string }[];
  translations: {
    ru: ArticleContent;
    uz?: ArticleContent;
    en?: ArticleContent;
  };
}

export interface UsefulHub {
  id: string;
  slug: string;
  title: string;
  description: string;
  seoTitle?: string;
  seoDescription?: string;
  image: string;
  articles: UsefulArticle[];
  translations?: {
    ru: { title: string; description: string };
    uz: { title: string; description: string };
    en: { title: string; description: string };
  };
}

export const usefulData: UsefulHub[] = [
  {
    id: 'spc-laminat',
    slug: 'spc-laminat',
    title: 'SPC Ламинат',
    description: 'SPC ламинат SARGO — руководство по выбору и применению. Что такое SPC, чем отличается от кварцвинила и ламината, как выбрать и уложить.',
    translations: {
      ru: {
        title: 'SPC ЛАМИНАТ.',
        description: 'SPC ламинат SARGO — руководство по выбору и применению. Что такое SPC, чем отличается от кварцвинила и ламината, как выбрать и уложить.'
      },
      uz: {
        title: 'SPC LAMINAT.',
        description: 'SARGO SPC laminati — tanlash va qo‘llash bo‘yicha qo‘llanma. SPC nima, kvartsvinil va laminatdan nimasi bilan farq qiladi, qanday tanlash va yotqizish.'
      },
      en: {
        title: 'SPC LAMINATE.',
        description: 'SARGO SPC laminate — a guide to selection and use. What SPC is, how it differs from stone vinyl and laminate, how to choose and install.'
      }
    },
    seoTitle: 'SPC ламинат SARGO: гид по выбору, характеристики и укладка',
    seoDescription: 'Полное руководство по SPC ламинату SARGO. Узнайте состав, преимущества перед LVT и ламинатом, правила выбора и технологию монтажа.',
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000',
    articles: [
      {
        id: 'chto-takoe-spc',
        slug: 'chto-takoe-spc',
        image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000',
        translations: {
          ru: {
            title: 'Что такое SPC ламинат: состав, структура, преимущества',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'SPC (Stone Plastic Composite) — это инновационное напольное покрытие на минерально-полимерной основе, сочетающее прочность камня и эстетику дерева. Относится к категории Rigid Core (жесткое ядро) и MMF (Multilayer Modular Floorings).',
            takeaways: [
              'SPC — это 100% водостойкое покрытие на основе известняка.',
              'Идеально подходит для ванных комнат и кухонь.',
              'Обладает экстремальной стабильностью размеров (до 200 м² без порогов).',
              'Совместим со всеми типами систем "теплый пол" (до +28°C).',
              'Срок службы в жилых помещениях — более 25 лет.',
              'Экологически безопасен: класс эмиссии E0/E1.'
            ],
            sections: [
              {
                id: 'definition',
                title: 'Что такое SPC?',
                content: 'SPC (Stone Plastic Composite) — это каменно-полимерный композит. В индустриальных стандартах (например, EN 16511) его часто классифицируют как "Rigid Core" — напольное покрытие с жестким основанием. В отличие от гибкого LVT, SPC плита не прогибается и сохраняет идеальную геометрию даже при резких перепадах температур.'
              },
              {
                id: 'composition',
                title: 'Состав материала',
                content: 'SPC (Stone Plastic Composite) — это каменно-полимерный композит. В индустриальных стандартах (например, EN 16511) его часто классифицируют как "Rigid Core" — напольное покрытие с жестким основанием. В отличие от гибкого LVT, SPC плита не прогибается и сохраняет идеальную геометрию даже при резких перепадах температур.'
              },
              {
                id: 'structure',
                title: 'Многослойная структура',
                type: 'accordion',
                content: 'SPC плита состоит из 5 ключевых слоев, каждый из которых выполняет свою техническую задачу:',
                items: [
                  {
                    title: '1. UV-защитное покрытие',
                    content: 'Тонкий слой, отвержденный ультрафиолетом. Защищает декоративный слой от выцветания на солнце и облегчает очистку поверхности от пятен.'
                  },
                  {
                    title: '2. Износостойкий слой (Wear Layer)',
                    content: 'Прозрачный слой ПВХ толщиной от 0.3 до 0.55 мм. Определяет класс износостойкости (33, 34 или 43). Защищает от царапин, когтей животных и абразивного износа.'
                  },
                  {
                    title: '3. Декоративный слой',
                    content: 'Высококачественная пленка с HD-печатью текстуры. Благодаря современным технологиям, SPC может идеально имитировать дерево, бетон, мрамор или терраццо.'
                  },
                  {
                    title: '4. Жесткое ядро (SPC Core)',
                    content: 'Основной несущий слой. Обладает высокой плотностью и содержит замковое соединение. Обеспечивает 100% водостойкость и ударопрочность.'
                  },
                  {
                    title: '5. Интегрированная подложка (опционально)',
                    content: 'Слой IXPE или EVA, приклеенный к плите. Гасит ударный шум, скрывает мелкие неровности основания и ускоряет монтаж.'
                  }
                ]
              },
              {
                id: 'advantages',
                title: 'Преимущества (без маркетинга)',
                list: [
                  'Стабильность размеров: расширение при нагреве в 5-7 раз ниже, чем у ламината.',
                  'Укладка без порогов: возможность единого полотна до 200-250 м².',
                  'Водостойкость: не разбухает при прямом контакте с водой.',
                  'Теплопроводность: прогревается быстрее плитки, идеален для теплого пола.',
                  'Пожаробезопасность: класс КМ2 (не поддерживает горение, низкое дымообразование).'
                ]
              },
              {
                id: 'comparison',
                title: 'Сравнение покрытий',
                type: 'tabs',
                items: [
                  {
                    label: 'SPC',
                    title: 'Stone Plastic Composite',
                    content: 'Максимально жесткое, стабильное и водостойкое. Идеально для всех помещений. Требует ровного основания.',
                    pros: ['Стабильность', 'Водостойкость', 'Теплый пол'],
                    cons: ['Требователен к полу', 'Тяжелый']
                  },
                  {
                    label: 'Ламинат',
                    title: 'HDF Laminate',
                    content: 'На основе древесной плиты (HDF). Боится воды, шумит при ходьбе, имеет высокие показатели расширения.',
                    pros: ['Цена', 'Разнообразие'],
                    cons: ['Боится воды', 'Пороги каждые 8-10м']
                  },
                  {
                    label: 'LVT',
                    title: 'Luxury Vinyl Tile',
                    content: 'Гибкая виниловая плитка. Более комфортная для ног, но "плывет" на солнце и требует идеального основания.',
                    pros: ['Комфорт', 'Тишина'],
                    cons: ['Нестабильность', 'Нужен клей']
                  }
                ]
              },
              {
                id: 'selection',
                title: 'Чек-лист выбора',
                type: 'checklist',
                content: 'Перед покупкой проверьте эти 7 параметров, чтобы пол служил десятилетиями:',
                items: [
                  'Класс износа: 33-34 для дома, 43 для офиса.',
                  'Толщина плиты: оптимально 4-5 мм.',
                  'Защитный слой: минимум 0.3 мм для спальни, 0.5 мм для кухни/коридора.',
                  'Тип замка: проверьте легкость защелкивания образцов.',
                  'Наличие подложки: встроенная экономит время и деньги.',
                  'Фаска: микрофаска делает стыки визуально аккуратнее.',
                  'Экологичность: ищите маркировку E0 или FloorScore.'
                ]
              },
              {
                id: 'myths',
                title: 'Мифы и Факты',
                type: 'myth-fact',
                items: [
                  {
                    myth: 'SPC можно укладывать на кривой пол, он же жесткий.',
                    fact: 'Наоборот. Жесткость SPC не позволяет ему "обгибать" неровности. На буграх замок встанет в напряжение и со временем сломается. Допуск — 2мм на 2 метра.'
                  },
                  {
                    myth: 'SPC холодный как плитка.',
                    fact: 'SPC имеет низкую теплоотдачу. Он комнатной температуры и на ощупь теплее керамогранита, но прохладнее натурального дерева.'
                  },
                  {
                    myth: 'Все SPC одинаковые по составу.',
                    fact: 'Нет. Дешевые варианты могут содержать вторичный пластик или мел, что делает их хрупкими. Качественный SPC SARGO использует только первичное сырье.'
                  }
                ]
              }
            ],
            faq: [
              { question: 'Нужен ли порог между комнатами?', answer: 'При площади до 200 м² и отсутствии резких перепадов температур SPC SARGO можно укладывать единым полотном без разрывов в дверных проемах.' },
              { question: 'Можно ли использовать пароочиститель?', answer: 'Да, но на средней мощности и не задерживаясь долго на одном месте. Высокая температура пара не вредит самой плите, но может повлиять на клеевой слой декоративной пленки.' },
              { question: 'Что лучше: встроенная подложка или отдельная?', answer: 'Встроенная подложка IXPE всегда лучше, так как она идеально приклеена к плите в заводских условиях, что исключает ошибки при выборе плотности сторонней подложки.' },
              { question: 'Как SPC ведет себя с домашними животными?', answer: 'Идеально. Защитный слой 0.5 мм устойчив к когтям даже крупных собак, а 100% влагостойкость защищает от любых "сюрпризов".' }
            ],
            sources: [
              { name: 'EN 16511: Многослойные модульные напольные покрытия', year: '2023', organization: 'CEN' },
              { name: 'Стандарты MMF (Multilayer Modular Flooring Association)', year: '2024' },
              { name: 'Технические регламенты SARGO Factory', year: '2024' }
            ]
          },
          uz: {
            title: 'SPC LAMINAT NIMA: TARKIBI, TUZILISHI, AFZALLIKLARI',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'SPC (Stone Plastic Composite) — bu tosh va yog‘och estetikasini birlashtirgan, mineral-polimer asosidagi innovatsion pol qoplamasi. U Rigid Core (qattiq yadro) va MMF (Multilayer Modular Floorings) toifasiga kiradi.',
            takeaways: [
              'SPC — ohaktosh asosidagi 100% suvga chidamli qoplama.',
              'Vannaxona va oshxonalar uchun ideal.',
              'Haddan tashqari o‘lchovli barqarorlikka ega (200 m² gacha bo‘shliqlarsiz).',
              'Har qanday turdagi "isitiladigan pol" tizimlari bilan mos keladi (28°C gacha).',
              'Turar-joylarda xizmat qilish muddati — 25 yildan ortiq.',
              'Ekologik xavfsiz: E0/E1 emissiya klassi.'
            ],
            sections: [
              {
                id: 'definition',
                title: 'SPC nima?',
                content: 'SPC (Stone Plastic Composite) — bu tosh-polimer kompozit. Sanoat standartlarida (masalan, EN 16511) u ko‘pincha "Rigid Core" — qattiq asosli pol qoplamasi sifatida tasniflanadi. G‘ilay LVT dan farqli o‘laroq, SPC plitasi egilmaydi va haroratning keskin o‘zgarishlarida ham mukammal geometriyani saqlaydi.'
              },
              {
                id: 'composition',
                title: 'Material tarkibi',
                content: 'SPC (Stone Plastic Composite) — bu tosh-polimer kompozit. Sanoat standartlarida (masalan, EN 16511) u ko‘pincha "Rigid Core" — qattiq asosli pol qoplamasi sifatida tasniflanadi. G‘ilay LVT dan farqli o‘laroq, SPC plitasi egilmaydi va haroratning keskin o‘zgarishlarida ham mukammal geometriyani saqlaydi.'
              },
              {
                id: 'structure',
                title: 'Ko‘p qatlamli tuzilish',
                type: 'accordion',
                content: 'SPC plitasi 5 ta asosiy qatlamdan iborat bo‘lib, har biri o‘zining texnik vazifasini bajaradi:',
                items: [
                  {
                    title: '1. UV-himoya qoplamasi',
                    content: 'Ultraviyole bilan qotirilgan yupqa qatlam. Dekorativ qatlamni quyoshdan rangsizlanishdan himoya qiladi va sirtni dog‘lardan tozalashni osonlashtiradi.'
                  },
                  {
                    title: '2. Chidamlilik qatlami (Wear Layer)',
                    content: '0,3 dan 0,55 mm gacha qalinlikdagi shaffof PVX qatlami. Chidamlilik klassini (33, 34 yoki 43) aniqlaydi. Tirnalishlar, hayvon tirnoqlari va abraziv eskirishdan himoya qiladi.'
                  },
                  {
                    title: '3. Dekorativ qatlam',
                    content: 'HD-bosma teksturali yuqori sifatli plyonka. Zamonaviy texnologiyalar tufayli SPC yog‘och, beton, marmar yoki terrakotani mukammal taqlid qila oladi.'
                  },
                  {
                    title: '4. Qattiq yadro (SPC Core)',
                    content: 'Asosiy tayanch qatlam. Yuqori zichlikka ega va qulf birikmasini o‘z ichiga oladi. 100% suvga chidamlilik va zarbga chidamlilikni ta\'minlaydi.'
                  },
                  {
                    title: '5. O‘rnatilgan taglik (ixtiyoriy)',
                    content: 'IXPE yoki EVA qatlami, plitaga yopishtirilgan. Zarba shovqinini kamaytiradi, poydevordagi mayda notekisliklarni yashiradi va o‘rnatishni tezlashtiradi.'
                  }
                ]
              },
              {
                id: 'advantages',
                title: 'Afzalliklari (marketingsiz)',
                list: [
                  'O‘lchovlarning barqarorligi: isitishda kengayishi laminatga nisbatan 5-7 marta past.',
                  'Bo‘shliqlarsiz o‘rnatish: 200-250 m² gacha yagona pol qoplamasi imkoniyati.',
                  'Suvga chidamliligi: suv bilan to‘g‘ridan-to‘g‘ri aloqada shishmaydi.',
                  'Issiqlik o‘tkazuvchanligi: plitadan tez isinadi, isitiladigan pol uchun ideal.',
                  'Yong‘in xavfsizligi: KM2 klassi (yonishni qo‘llab-quvvatlamaydi, kam tutun hosil qiladi).'
                ]
              },
              {
                id: 'comparison',
                title: 'Qoplamalarni taqqoslash',
                type: 'tabs',
                items: [
                  {
                    label: 'SPC',
                    title: 'Stone Plastic Composite',
                    content: 'Maksimal qattiq, barqaror va suvga chidamli. Barcha xonalar uchun ideal. Tekis poydevorni talab qiladi.',
                    pros: ['Barqarorlik', 'Suvga chidamlilik', 'Isitiladigan pol'],
                    cons: ['Polga talabchan', 'Og‘ir']
                  },
                  {
                    label: 'Laminat',
                    title: 'HDF Laminate',
                    content: 'Daraxt tolasi plitasi (HDF) asosida. Suvdan qo‘rqadi, yurishda shovqin chiqaradi, kengayish ko‘rsatkichlari yuqori.',
                    pros: ['Narx', 'Rang-baranglik'],
                    cons: ['Suvdan qo‘rqadi', 'Har 8-10 m da bo‘shliqlar']
                  },
                  {
                    label: 'LVT',
                    title: 'Luxury Vinyl Tile',
                    content: 'Eg‘iluvchan vinil plitka. Oyoq uchun qulayroq, lekin quyoshda "suzadi" va mukammal poydevorni talab qiladi.',
                    pros: ['Qulaylik', 'Jimlik'],
                    cons: ['Barqaror emas', 'Yelim kerak']
                  }
                ]
              },
              {
                id: 'selection',
                title: 'Tanlash uchun nazorat ro‘yxati',
                type: 'checklist',
                content: 'Sotib olishdan oldin ushbu 7 ta parametrni tekshiring, shunda pol o‘n yillar davomida xizmat qiladi:',
                items: [
                  'Chidamlilik klassi: uy uchun 33-34, ofis uchun 43.',
                  'Plita qalinligi: optimal 4-5 mm.',
                  'Himoya qatlami: yotoqxona uchun kamida 0,3 mm, oshxona/koridor uchun 0,5 mm.',
                  'Qulf turi: namunalarning oson qulfini tekshiring.',
                  'Taglik mavjudligi: o‘rnatilgani vaqt va pulni tejaydi.',
                  'Faska: mikro-faska bo‘g‘inlarni vizual ravishda toza ko‘rsatadi.',
                  'Ekologik tozaligi: E0 yoki FloorScore belgisini qidiring.'
                ]
              },
              {
                id: 'myths',
                title: 'Miflar va Faktlar',
                type: 'myth-fact',
                items: [
                  {
                    myth: 'SPCni notekis polga yotqizish mumkin, chunki u qattiq.',
                    fact: 'Aksincha. SPCning qattiqligi uni notekisliklarni "aylanib o‘tishiga" yo‘l qo‘ymaydi. Balandliklarda qulf kuchlanish ostida qoladi va vaqt o‘tishi bilan sinadi. Ruxsat etilgan notekislik — 2 mm.'
                  },
                  {
                    myth: 'SPC plitkadek sovuq.',
                    fact: 'SPC issiqlikni kam o‘tkazadi. U xona haroratida bo‘ladi va qo‘lda chinni plitadan issiqroq, lekin tabiiy yog‘ochdan salqinroq his qilinadi.'
                  },
                  {
                    myth: 'Barcha SPC tarkibi bir xil.',
                    fact: 'Yo‘q. Arzon variantlar ikkilamchi plastmassa yoki bo‘rni o‘z ichiga olishi mumkin, bu ularni mo‘rt qiladi. Sifatli SPC SARGO faqat birlamchi xomashyodan foydalanadi.'
                  }
                ]
              }
            ],
            faq: [
              { question: 'Xonalar orasida bo‘shliq kerakmi?', answer: '200 m² gacha bo‘lgan maydonlarda va haroratning keskin o‘zgarishlari bo‘lmagan taqdirda, SPC SARGO ni eshik teshiklarida bo‘shliqlarsiz yagona pol sifatida yotqizish mumkin.' },
              { question: 'Bug‘li tozalagichdan foydalanish mumkinmi?', answer: 'Ha, lekin o‘rtacha quvvatda va bir joyda uzoq turmasdan. Bug‘ning yuqori harorati plitaga zarar bermaydi, lekin dekorativ plyonkaning yelim qatlamiga ta\'sir qilishi mumkin.' },
              { question: 'O‘rnatilgan taglik yoki alohida taglik yaxshiroqmi?', answer: 'O‘rnatilgan IXPE tagligi har doim yaxshiroq, chunki u zavod sharoitida plitaga mukammal yopishtirilgan, bu esa tashqi taglikni tanlashda xatoliklarni istisno qiladi.' },
              { question: 'SPC uy hayvonlari bilan qanday munosabatda bo‘ladi?', answer: 'Ideal. 0,5 mm himoya qatlami hatto yirik itlarning tirnoqlariga ham chidamli, 100% suvga chidamliligi esa har qanday "sürprizlardan" himoya qiladi.' }
            ]
          },
          en: {
            title: 'WHAT IS SPC LAMINATE: COMPOSITION, STRUCTURE, BENEFITS',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'SPC (Stone Plastic Composite) is an innovative flooring based on a mineral-polymer composite, combining the strength of stone with the aesthetics of wood. It falls under the Rigid Core category and MMF (Multilayer Modular Floorings).',
            takeaways: [
              'SPC is a 100% waterproof flooring based on limestone.',
              'Ideal for bathrooms and kitchens.',
              'Possesses extreme dimensional stability (up to 200 m² without thresholds).',
              'Compatible with all types of "underfloor heating" systems (up to +28°C).',
              'Service life in residential premises is over 25 years.',
              'Environmentally safe: E0/E1 emission class.'
            ],
            sections: [
              {
                id: 'definition',
                title: 'What is SPC?',
                content: 'SPC (Stone Plastic Composite) is a stone-polymer composite. In industrial standards (e.g., EN 16511), it is often classified as "Rigid Core" – flooring with a rigid base. Unlike flexible LVT, SPC planks do not bend and maintain perfect geometry even with sharp temperature fluctuations.'
              },
              {
                id: 'composition',
                title: 'Material Composition',
                content: 'SPC (Stone Plastic Composite) is a stone-polymer composite. In industrial standards (e.g., EN 16511), it is often classified as "Rigid Core" – flooring with a rigid base. Unlike flexible LVT, SPC planks do not bend and maintain perfect geometry even with sharp temperature fluctuations.'
              },
              {
                id: 'structure',
                title: 'Multi-layer Structure',
                type: 'accordion',
                content: 'An SPC plank consists of 5 key layers, each performing its technical function:',
                items: [
                  {
                    title: '1. UV Protective Coating',
                    content: 'A thin layer cured by ultraviolet light. Protects the decorative layer from fading in the sun and makes surface stain cleaning easier.'
                  },
                  {
                    title: '2. Wear Layer',
                    content: 'A transparent PVC layer from 0.3 to 0.55 mm thick. Determines the wear resistance class (33, 34, or 43). Protects against scratches, animal claws, and abrasive wear.'
                  },
                  {
                    title: '3. Decorative Layer',
                    content: 'High-quality film with HD texture printing. Thanks to modern technologies, SPC can perfectly imitate wood, concrete, marble, or terrazzo.'
                  },
                  {
                    title: '4. SPC Core',
                    content: 'The main load-bearing layer. It has high density and incorporates a click system. Ensures 100% water resistance and impact resistance.'
                  },
                  {
                    title: '5. Integrated Underlay (optional)',
                    content: 'An IXPE or EVA layer glued to the plank. Dampens impact noise, hides minor subfloor imperfections, and speeds up installation.'
                  }
                ]
              },
              {
                id: 'advantages',
                title: 'Advantages (without marketing)',
                list: [
                  'Dimensional stability: thermal expansion is 5-7 times lower than laminate.',
                  'Installation without thresholds: possibility of a single continuous floor up to 200-250 m².',
                  'Water resistance: does not swell upon direct contact with water.',
                  'Thermal conductivity: heats up faster than tiles, ideal for underfloor heating.',
                  'Fire safety: KM2 class (does not support combustion, low smoke emission).'
                ]
              },
              {
                id: 'comparison',
                title: 'Coating Comparison',
                type: 'tabs',
                items: [
                  {
                    label: 'SPC',
                    title: 'Stone Plastic Composite',
                    content: 'Extremely rigid, stable, and waterproof. Ideal for all rooms. Requires a level subfloor.',
                    pros: ['Stability', 'Water resistance', 'Underfloor heating'],
                    cons: ['Demanding subfloor requirements', 'Heavy']
                  },
                  {
                    label: 'Laminate',
                    title: 'HDF Laminate',
                    content: 'Based on wood fiberboard (HDF). Afraid of water, noisy when walked on, has high expansion rates.',
                    pros: ['Price', 'Variety'],
                    cons: ['Afraid of water', 'Thresholds every 8-10m']
                  },
                  {
                    label: 'LVT',
                    title: 'Luxury Vinyl Tile',
                    content: 'Flexible vinyl tile. More comfortable underfoot, but "floats" in the sun and requires a perfect subfloor.',
                    pros: ['Comfort', 'Quiet'],
                    cons: ['Instability', 'Requires glue']
                  }
                ]
              },
              {
                id: 'selection',
                title: 'Selection Checklist',
                type: 'checklist',
                content: 'Before purchasing, check these 7 parameters to ensure the floor lasts for decades:',
                items: [
                  'Wear class: 33-34 for homes, 43 for offices.',
                  'Plank thickness: optimally 4-5 mm.',
                  'Wear layer: minimum 0.3 mm for bedrooms, 0.5 mm for kitchens/hallways.',
                  'Click system: check how easily samples lock together.',
                  'Integrated underlay: saves time and money.',
                  'Bevel: a micro-bevel makes seams visually neater.',
                  'Eco-friendliness: look for E0 or FloorScore certification.'
                ]
              },
              {
                id: 'myths',
                title: 'Myths and Facts',
                type: 'myth-fact',
                items: [
                  {
                    myth: 'SPC can be laid on an uneven floor because it is rigid.',
                    fact: 'On the contrary. SPC\'s rigidity does not allow it to "bend" around imperfections. On bumps, the lock will be stressed and eventually break. Tolerance is 2mm per 2 meters.'
                  },
                  {
                    myth: 'SPC is as cold as tile.',
                    fact: 'SPC has low thermal conductivity. It is at room temperature and feels warmer than porcelain stoneware to the touch, but cooler than natural wood.'
                  },
                  {
                    myth: 'All SPC is the same in composition.',
                    fact: 'No. Cheap options may contain recycled plastic or chalk, making them brittle. High-quality SPC SARGO uses only virgin raw materials.'
                  }
                ]
              }
            ],
            faq: [
              { question: 'Do I need a threshold between rooms?', answer: 'For areas up to 200 m² and without sharp temperature changes, SPC SARGO can be laid as a single continuous floor without breaks in doorways.' },
              { question: 'Can I use a steam cleaner?', answer: 'Yes, but on medium power and without lingering in one spot for too long. High steam temperature does not harm the plank itself, but it can affect the adhesive layer of the decorative film.' },
              { question: 'What is better: integrated underlay or separate?', answer: 'Integrated IXPE underlay is always better, as it is perfectly glued to the plank under factory conditions, which eliminates errors when choosing the density of an external underlay.' },
              { question: 'How does SPC behave with pets?', answer: 'Ideally. The 0.5 mm wear layer is resistant to the claws of even large dogs, and 100% water resistance protects against any "surprises".' }
            ]
          }
        }
      },
      {
        id: 'kupit-cena',
        slug: 'kupit-cena',
        image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000',
        translations: {
          ru: {
            title: 'Купить SPC ламинат: цена и как выбрать',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Практическое руководство для тех, кто планирует покупку SPC покрытия. Разбираем, из чего складывается реальная стоимость, как отличить качественный продукт от подделки и где совершить покупку без лишних рисков.',
            takeaways: [
              'Конечная стоимость — это не только цена за м², но и расходники + монтаж.',
              'Официальный дилер — самая безопасная точка покупки с гарантией.',
              'Запас 7-10% обязателен для компенсации подрезки.',
              'Дешевый SPC часто содержит хрупкое вторичное сырье.',
              'Интегрированная подложка экономит до 15% общего бюджета.'
            ],
            sections: [
              {
                id: 'buying-steps',
                title: 'Коротко: как купить за 5 шагов',
                type: 'buying-steps',
                items: [
                  'Замерьте точную площадь и прибавьте 7-10% на запас.',
                  'Выберите класс износа (33-34 для дома, 43 для офиса).',
                  'Определитесь с цветом и фаской под ваш дизайн.',
                  'Сравните предложения: цена м² + подложка + доставка.',
                  'Проверьте наличие сертификатов и условия гарантии.'
                ]
              },
              {
                id: 'price-factors',
                title: 'От чего на самом деле зависит цена',
                content: 'Цена на SPC ламинат не берется "с потолка". Она жестко привязана к техническим характеристикам и логистике. \n\nОсновные факторы:\n• Защитный слой (Wear Layer): Чем толще прозрачный слой ПВХ (0.3 мм vs 0.55 мм), тем дороже производство.\n• Плотность ядра: Качественный SPC имеет плотность 2000+ кг/м³. Дешевые аналоги "разбавляют" мелом, что делает их хрупкими.\n• Замковая система: Лицензионные замки (Uniclic, Valinge) надежнее безымянных аналогов.\n• Встроенная подложка: Наличие слоя IXPE повышает цену за м², но избавляет от покупки отдельной подложки.\n• Дизайн и тиснение: Глубокое тиснение "в регистр" (синхронизация рисунка и рельефа) стоит дороже обычной гладкой печати.'
              },
              {
                id: 'calculator',
                title: 'Калькулятор покупки (ориентир)',
                type: 'calculator'
              },
              {
                id: 'where-to-buy',
                title: 'Где покупать: варианты и риски',
                type: 'comparison-table',
                items: [
                  {
                    channel: 'Официальный дилер',
                    pros: 'Гарантия производителя, наличие всех сертификатов, профессиональная консультация.',
                    cons: 'Цена может быть чуть выше минимально рыночной.',
                    risk: 'Минимальный. Прямые поставки с завода.',
                    context: 'Лучший выбор для тех, кому важна долговечность и сервис.'
                  },
                  {
                    channel: 'Строительный гипермаркет',
                    pros: 'Можно купить "здесь и сейчас", часто бывают акции.',
                    cons: 'Сложно найти редкие коллекции, консультанты общего профиля.',
                    risk: 'Средний. Возможны проблемы с хранением на складах.',
                    context: 'Подходит для стандартных решений среднего сегмента.'
                  },
                  {
                    channel: 'Маркетплейсы',
                    pros: 'Самая низкая цена, удобная доставка.',
                    cons: 'Нельзя потрогать "вживую", риск пересорта или боя при доставке.',
                    risk: 'Высокий. Сложности с возвратом и рекламациями.',
                    context: 'Вариант для опытных покупателей, знающих конкретный артикул.'
                  },
                  {
                    channel: 'Рынки и склады',
                    pros: 'Максимальная экономия.',
                    cons: 'Отсутствие гарантии, сомнительное происхождение товара.',
                    risk: 'Критический. Можно купить подделку из вторичного сырья.',
                    context: 'Только на свой страх и риск.'
                  }
                ]
              },
              {
                id: 'criteria',
                title: '10 критериев правильного выбора',
                type: 'accordion',
                items: [
                  {
                    title: '1. Сценарий помещения',
                    content: 'Для ванной важна 100% герметичность замков, для кухни — стойкость к пятнам, для гостиной — акустический комфорт.'
                  },
                  {
                    title: '2. Тип основания',
                    content: 'SPC требует идеально ровного пола (перепад не более 2 мм на 2 метра). Если пол кривой — замок сломается.'
                  },
                  {
                    title: '3. Класс износостойкости',
                    content: 'Не гонитесь за 43 классом для спальни. 33-34 класса достаточно для любого жилого помещения.'
                  },
                  {
                    title: '4. Толщина защитного слоя',
                    content: '0.3 мм — для жилых зон, 0.5-0.55 мм — для зон с высокой проходимостью (коридор, офис).'
                  },
                  {
                    title: '5. Замковая система',
                    content: 'Ищите проверенные бренды замков. Плохой замок начнет скрипеть или разойдется через год.'
                  },
                  {
                    title: '6. Наличие фаски',
                    content: 'Фаска скрывает мелкие неровности и подчеркивает структуру доски. Без фаски пол выглядит как монолитный линолеум.'
                  },
                  {
                    title: '7. Экологичность (VOC)',
                    content: 'Проверьте отсутствие фталатов и тяжелых металлов. Сертификат FloorScore или французский A+ обязателен.'
                  },
                  {
                    title: '8. Геометрия плиты',
                    content: 'Достаньте пару досок из пачки. Они должны идеально стыковаться без щелей по всей длине.'
                  },
                  {
                    title: '9. Ремонтопригодность',
                    content: 'Узнайте, можно ли заменить одну доску в центре комнаты без разбора всего пола.'
                  },
                  {
                    title: '10. Условия гарантии',
                    content: 'Читайте мелкий шрифт: гарантия часто действует только при использовании одобренной подложки.'
                  }
                ]
              },
              {
                id: 'red-flags',
                title: 'Красные флаги при покупке',
                type: 'red-flags',
                items: [
                  {
                    title: 'Слишком низкая цена',
                    content: 'Качественные компоненты стоят денег. Цена в 2 раза ниже рыночной говорит об использовании токсичного вторсырья.'
                  },
                  {
                    title: 'Резкий химический запах',
                    content: 'Хороший SPC не пахнет. Запах свидетельствует о выделении формальдегидов или дешевых пластификаторов.'
                  },
                  {
                    title: 'Хрупкие замки',
                    content: 'Если замок легко ломается пальцем — пол не выдержит нагрузки мебели.'
                  },
                  {
                    title: 'Разный оттенок в партиях',
                    content: 'Проверяйте номер партии на всех коробках. Цвет может отличаться, что будет заметно после укладки.'
                  }
                ]
              },
              {
                id: 'how-to-save',
                title: 'Как не переплатить',
                content: 'Считайте "полную стоимость владения", а не цену за м². \n\n• Берите SPC со встроенной подложкой — это дешевле, чем покупать ее отдельно.\n• Ищите акции на коллекции прошлого года (технически они такие же).\n• Рассчитывайте запас точно: 5% для прямой укладки, 10-12% для диагональной.\n• Не экономьте на мастере — переделка испорченного материала обойдется в цену нового пола.'
              },
              {
                id: 'quality-check',
                title: 'Чек-лист качества (проверьте перед оплатой)',
                type: 'checklist',
                items: [
                  'Целостность упаковок: нет замятых углов коробки.',
                  'Маркировка: везде указана одна и та же партия (Batch/Lot).',
                  'Соответствие цвета образцу в салоне.',
                  'Наличие инструкции по укладке на языке страны.',
                  'Сертификат соответствия пожарной безопасности (КМ2).'
                ]
              },
              {
                id: 'seller-questions',
                title: 'Что спросить у продавца',
                type: 'seller-questions',
                items: [
                  'Какая плотность у плиты? (Должно быть около 2000 кг/м³)',
                  'Это первичное сырье или вторичное?',
                  'Какая гарантия на замковые соединения при укладке на теплый пол?',
                  'Можно ли использовать подложку толщиной более 2 мм? (Ответ должен быть "нет")',
                  'Какое расширение у этой коллекции на 10 погонных метров?'
                ]
              }
            ],
            faq: [
              { question: 'Какая цена считается "нормальной" для качественного SPC?', answer: 'Цены варьируются в зависимости от региона и коллекции, но качественный продукт с защитным слоем 0.5 мм не может стоить дешевле среднего по рынку. Ориентируйтесь на средний сегмент проверенных брендов.' },
              { question: 'Нужно ли покупать клей?', answer: 'Замковый SPC укладывается "плавающим способом" без клея. Клей нужен только для клеевых коллекций LVT или в редких коммерческих случаях.' },
              { question: 'Можно ли класть на старый линолеум?', answer: 'Мы не рекомендуем. Линолеум слишком мягкий, под нагрузкой мебели замки SPC могут прогнуться и сломаться.' },
              { question: 'Сколько времени SPC должен лежать в квартире перед укладкой?', answer: 'Минимум 48 часов в горизонтальном положении для акклиматизации к температуре и влажности помещения.' },
              { question: 'Что делать, если одна плашка повредилась?', answer: 'При замковой укладке можно разобрать часть пола и заменить плашку, либо воспользоваться специальными восковыми корректорами для мелких царапин.' },
              { question: 'Как ведет себя SPC под тяжелой мебелью?', answer: 'Благодаря высокой плотности ядра SPC не оставляет вмятин от ножек шкафов и диванов, в отличие от линолеума или пробки.' },
              { question: 'Можно ли укладывать SPC на балконе?', answer: 'Только если балкон застеклен и утеплен. Резкие перепады температур от -20 до +40 могут повредить замок.' },
              { question: 'Нужна ли пароизоляция?', answer: 'Для бетонных субфлоров всегда рекомендуется укладывать полиэтиленовую пленку 200 мкм для защиты от остаточной влаги стяжки.' }
            ]
          },
          uz: {
            title: 'SPC LAMINAT SOTIB OLISH: NARX VA QANDAY TANLASH',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'SPC qoplamasini sotib olishni rejalashtirganlar uchun amaliy qo‘llanma. Haqiqiy xarajat nimalardan iborat ekanligini va sifatli mahsulotni qhow tanlashni bilib oling.',
            takeaways: [
              'Yakuniy xarajat — bu nafaqat m² narxi, balki qo‘shimcha materiallar va o‘rnatish xarajatlari.',
              'Rasmiy diler — kafolatlangan va eng xavfsiz xarid nuqtasi.',
              'Kesish uchun 7-10% zaxira bo‘lishi shart.',
              'Arzon SPC ko‘pincha mo‘rt ikkilamchi xomashyodan tayyorlanadi.',
              'O‘rnatilgan taglik vaqt va pulni tejaydi.'
            ],
            sections: [
              {
                id: 'buying-steps',
                title: 'Qisqacha: 5 qadamda qanday sotib olish mumkin',
                type: 'buying-steps',
                items: [
                  'Aniq maydonni o‘lchang va 7-10% zaxira qo‘shing.',
                  'Chidamlilik klassini tanlang (uy uchun 33-34).',
                  'Dizayningizga mos rang va faskani tanlang.',
                  'Takliflarni solishtiring: narx m² + taglik + yetkazib berish.',
                  'Sertifikatlar va kafolat shartlarini tekshiring.'
                ]
              },
              {
                id: 'price-factors',
                title: 'Narx nimalarga bog‘liq',
                content: 'SPC laminat narxi uning texnik xususiyatlariga bog‘liq: himoya qatlami qalinligi, asos zichligi va brendning ishonchliligi. Sifatli SPC zichligi 2000 kg/m³ dan kam bo‘lmasligi kerak.'
              },
              {
                id: 'calculator',
                title: 'Xarid kalkulyatori (taxminiy)',
                type: 'calculator'
              },
              {
                id: 'red-flags',
                title: 'Xarid qilishdagi "qizil bayroqlar"',
                type: 'red-flags',
                items: [
                  { title: 'Juda past narx', content: 'Sifatli komponentlar qimmat turadi. Bozor narxidan 2 baravar past narx sifatsiz xomashyodan dalolat beradi.' },
                  { title: 'O‘tkir kimyoviy hid', content: 'Yaxshi SPC hidlanmaydi. Hid zararli moddalar borligini anglatadi.' },
                  { title: 'Mo‘rt qulflar', content: 'Agar qulf barmog‘ingiz bilan oson sinsa, u mebel yukiga bardosh bera olmaydi.' }
                ]
              },
              {
                id: 'quality-check',
                title: 'Sifat nazorati (to‘lovdan oldin tekshiring)',
                type: 'checklist',
                items: [
                  'Qadoqlarning butunligi: qutining burchaklari ezilmagan.',
                  'Markirovka: barcha qutilarda bir xil partiya (Batch/Lot) raqami ko‘rsatilganligi.',
                  'Rangning namunaga mosligi.',
                  'Mamlakat tilidagi o‘rnatish bo‘yicha ko‘rsatmalar mavjudligi.',
                  'Yong‘in xavfsizligi sertifikati (KM2) mavjudligi.'
                ]
              },
              {
                id: 'seller-questions',
                title: 'Sotuvchidan nimalarni so‘rash kerak',
                type: 'seller-questions',
                items: [
                  'Asos zichligi qancha? (Taxminan 2000 kg/m³ bo‘lishi kerak)',
                  'Bu birlamchi xomashyomi yoki ikkilamchi?',
                  'Issiq polga yotqizishda qulflar uchun kafolat bormi?',
                  'Qalinligi 2 mm dan ortiq taglikdan foydalanish mumkinmi? (Javob "yo‘q" bo‘lishi kerak)',
                  'Ushbu kolleksiyaning 10 metr uzunlikdagi kengayishi qancha?'
                ]
              }
            ],
            faq: [
              { question: 'Yelim sotib olish kerakmi?', answer: 'Yo‘q, qulfli SPC "suzuvchi usul" bilan yelimsiz yotqiziladi. Elim faqat kley-bazali LVT kolleksiyalari yoki kamdan-kam hollarda tijoriy maqsadlarda kerak bo‘ladi.' },
              { question: 'Eski linoleum ustiga yotqizish mumkinmi?', answer: 'Tavsiya etilmaydi. Linoleum juda yumshoq, mebel bosimi ostida SPC qulflari egilib, sinishi mumkin.' },
              { question: 'Ukladkadan oldin SPC necha vaqt turishi kerak?', answer: 'Xona harorati va namligiga moslashishi uchun kamida 48 soat gorizontal holatda turishi kerak.' },
              { question: 'Agar bitta list shikastlangan bo‘lsa, nima qilish kerak?', answer: 'Qulfli yotqizishda polning bir qismini ochib, listni almashtirish mumkin, yoki mayda tirnalishlar uchun maxsus mum qalamlaridan foydalanish mumkin.' },
              { question: 'Og‘ir mebel ostida SPC qanday turadi?', answer: 'Yuqori zichlikdagi asos tufayli SPC shkaflar va divan oyoqlaridan iz qoldirmaydi, linoleum yoki пробкaga farqli o‘laroq.' },
              { question: 'Balkonga SPC yotqizish mumkinmi?', answer: 'Faqat balkon yopiq va izolyatsiya qilingan bo‘lsa. Haroratning keskin o‘zgarishi (-20 dan +40 gacha) qulfga zarar yetkazishi mumkin.' },
              { question: 'Bug‘ izolyatsiyasi kerakmi?', answer: 'Beton asosga birinchi qavat sifatida har doim namlikdan himoya qilish uchun 200 mkm polietilen plyonka yotqizish tavsiya etiladi.' }
            ]
          },
          en: {
            title: 'BUY SPC LAMINATE: PRICE AND HOW TO CHOOSE',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'A practical guide for those planning to purchase SPC flooring. We analyze what makes up the real cost, how to distinguish a quality product, and where to buy without risks.',
            takeaways: [
              'The final cost includes materials + underlay + installation.',
              'Official dealers provide the best warranty and support.',
              '7-10% waste factor is mandatory for trimming.',
              'Cheap SPC often contains brittle recycled materials.',
              'Integrated underlay saves up to 15% of the total budget.'
            ],
            sections: [
              {
                id: 'buying-steps',
                title: 'In short: How to buy in 5 steps',
                type: 'buying-steps',
                items: [
                  'Measure the exact area and add 7-10% for waste.',
                  'Select the wear class (33-34 for home, 43 for office).',
                  'Choose color and bevel type for your design.',
                  'Compare offers: price per sqm + underlay + delivery.',
                  'Check certificates and warranty terms.'
                ]
              },
              {
                id: 'price-factors',
                title: 'What determines the price',
                content: 'The cost of SPC laminate is directly tied to its technical characteristics and logistics. \n\nKey factors:\n• Wear Layer: The thicker the transparent PVC layer (0.3 mm vs 0.55 mm), the more expensive the production.\n• Core Density: Quality SPC has a density of 2000+ kg/m³. Cheap alternatives are "diluted" with chalk, making them brittle.\n• Click System: Licensed locks (Uniclic, Valinge) are more reliable but require royalties to developers.\n• Integrated Underlay: The presence of an IXPE layer increases the price per sqm but eliminates the need to buy a separate underlay.\n• Design and Embossing: Deep "in-register" embossing (synchronizing pattern and relief) costs more than standard smooth printing.'
              },
              {
                id: 'calculator',
                title: 'Purchase Calculator (Estimate)',
                type: 'calculator'
              },
              {
                id: 'where-to-buy',
                title: 'Where to buy: options and risks',
                type: 'comparison-table',
                items: [
                  {
                    channel: 'Official Dealer',
                    pros: 'Manufacturer\'s warranty, all certificates available, professional consultation.',
                    cons: 'Price may be slightly higher than the minimum market price.',
                    risk: 'Minimal. Direct supply from the factory.',
                    context: 'Best choice for those who value durability and service.'
                  },
                  {
                    channel: 'DIY Hypermarket',
                    pros: 'Can buy "here and now", often have promotions.',
                    cons: 'Difficult to find rare collections, general consultants.',
                    risk: 'Medium. Possible storage issues in warehouses.',
                    context: 'Suitable for standard mid-range solutions.'
                  },
                  {
                    channel: 'Marketplaces',
                    pros: 'Lowest price, convenient delivery.',
                    cons: 'Cannot "touch and feel", risk of wrong item or damage during delivery.',
                    risk: 'High. Difficulties with returns and claims.',
                    context: 'Option for experienced buyers who know the specific item number.'
                  },
                  {
                    channel: 'Markets and Warehouses',
                    pros: 'Maximum savings.',
                    cons: 'No warranty, questionable product origin.',
                    risk: 'Critical. You might buy a counterfeit made from recycled materials.',
                    context: 'At your own risk.'
                  }
                ]
              },
              {
                id: 'criteria',
                title: '10 criteria for the right choice',
                type: 'accordion',
                items: [
                  {
                    title: '1. Room Scenario',
                    content: 'For bathrooms, 100% lock tightness is important; for kitchens, stain resistance; for living rooms, acoustic comfort.'
                  },
                  {
                    title: '2. Base Type',
                    content: 'SPC requires a perfectly level floor (no more than 2mm deviation over 2 meters). If the floor is uneven, the lock will be stressed and break over time.'
                  },
                  {
                    title: '3. Wear Resistance Class',
                    content: 'Don\'t chase class 43 for a bedroom. Class 33-34 is sufficient for any residential space.'
                  },
                  {
                    title: '4. Protective Layer Thickness',
                    content: '0.3 mm for residential areas, 0.5-0.55 mm for high-traffic zones (hallway, office).'
                  },
                  {
                    title: '5. Click System',
                    content: 'Look for proven lock brands. A bad lock will start creaking or come apart within a year.'
                  },
                  {
                    title: '6. Bevel Presence',
                    content: 'A bevel hides minor imperfections and emphasizes the plank\'s texture. Without a bevel, the floor looks like monolithic linoleum.'
                  },
                  {
                    title: '7. Eco-friendliness (VOC)',
                    content: 'Check for the absence of phthalates and heavy metals. FloorScore or French A+ certification is mandatory.'
                  },
                  {
                    title: '8. Plank Geometry',
                    content: 'Take a couple of planks from the pack. They should fit together perfectly without gaps along the entire length.'
                  },
                  {
                    title: '9. Repairability',
                    content: 'Find out if a single plank in the middle of the room can be replaced without disassembling the entire floor.'
                  },
                  {
                    title: '10. Warranty Conditions',
                    content: 'Read the fine print: the warranty is often valid only when using an approved underlay.'
                  }
                ]
              },
              {
                id: 'red-flags',
                title: 'Red Flags when buying',
                type: 'red-flags',
                items: [
                  {
                    title: 'Too low price',
                    content: 'Quality components cost money. A price 2 times lower than the market average indicates the use of toxic recycled materials.'
                  },
                  {
                    title: 'Sharp chemical odor',
                    content: 'Good SPC does not smell. An odor indicates the release of formaldehyde or cheap plasticizers.'
                  },
                  {
                    title: 'Brittle locks',
                    content: 'If the lock breaks easily with a finger, the floor will not withstand furniture loads.'
                  },
                  {
                    title: 'Different shades in batches',
                    content: 'Check the batch number on all boxes. The color may differ, which will be noticeable after installation.'
                  }
                ]
              },
              {
                id: 'how-to-save',
                title: 'How not to overpay',
                content: 'Calculate the "total cost of ownership", not just the price per sqm. \n\n• Choose SPC with integrated underlay – it\'s cheaper than buying it separately.\n• Look for sales on previous year\'s collections (technically they are the same).\n• Calculate waste precisely: 5% for straight installation, 10-12% for diagonal.\n• Don\'t skimp on the installer – redoing damaged material will cost as much as a new floor.'
              },
              {
                id: 'quality-check',
                title: 'Quality Checklist (check before payment)',
                type: 'checklist',
                items: [
                  'Package integrity: no dented box corners.',
                  'Marking: the same batch (Batch/Lot) is indicated everywhere.',
                  'Color matches the sample in the showroom.',
                  'Presence of installation instructions in the country\'s language.',
                  'Fire safety compliance certificate (KM2).'
                ]
              },
              {
                id: 'seller-questions',
                title: 'What to ask the seller',
                type: 'seller-questions',
                items: [
                  'What is the core density? (Should be around 2000 kg/m³)',
                  'Is it virgin or recycled material?',
                  'What is the warranty on the click joints when installed on underfloor heating?',
                  'Can an underlay thicker than 2 mm be used? (The answer should be "no")',
                  'What is the expansion of this collection over 10 linear meters?'
                ]
              }
            ],
            faq: [
              { question: 'What price is considered "normal" for quality SPC?', answer: 'Prices vary depending on the region and collection, but a quality product with a 0.5 mm wear layer cannot cost less than the market average. Aim for the mid-range of proven brands.' },
              { question: 'Do I need to buy glue?', answer: 'Click-system SPC is installed using the floating method without adhesive. Glue is only needed for glue-down LVT collections or in rare commercial cases.' },
              { question: 'Can I lay it on old linoleum?', answer: 'We do not recommend. Linoleum is too soft; under furniture pressure, SPC locks can bend and break.' },
              { question: 'How long should SPC acclimate before installation?', answer: 'At least 48 hours in a horizontal position to acclimatize to the room temperature and humidity.' },
              { question: 'What to do if one plank is damaged?', answer: 'With click installation, you can disassemble part of the floor and replace the plank, or use special wax repair crayons for minor scratches.' },
              { question: 'How does SPC behave under heavy furniture?', answer: 'Thanks to its high core density, SPC does not leave indentations from cabinet and sofa legs, unlike linoleum or cork.' },
              { question: 'Can SPC be installed on a balcony?', answer: 'Only if the balcony is glazed and insulated. Sharp temperature fluctuations from -20 to +40 can damage the lock.' },
              { question: 'Is a vapor barrier needed?', answer: 'For concrete subfloors, it is always recommended to lay a 200-micron polyethylene film first to protect against residual screed moisture.' }
            ]
          }
        }
      },
      {
        id: 'spc-vs-laminat',
        slug: 'spc-vs-laminat',
        image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000',
        translations: {
          ru: {
            title: 'SPC или ламинат: что выбрать для дома и бизнеса',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Глубокое практическое сравнение каменно-полимерного композита (SPC) и классического ламината на HDF-основе. Разбираем, что выбрать для разных условий эксплуатации, учитывая бюджет, долговечность и скрытые риски.',
            takeaways: [
              'SPC — 100% водостойкость, ламинат — влагостойкость с ограничениями.',
              'Для коммерции с высоким трафиком SPC 43 класса — золотой стандарт.',
              'Ламинат выигрывает в бюджете при укладке в сухих жилых зонах.',
              'SPC требует идеально ровного основания (перепад до 2мм/2м).',
              'Оба материала совместимы с теплым полом, но SPC эффективнее проводит тепло.',
              'В новостройках с риском усадки SPC стабильнее за счет жесткого ядра.'
            ],
            sections: [],
            faq: []
          },
          uz: {
            title: 'SPC YOKI LAMINAT: UY VA BIZNES UCHUN NIMA TANLASH',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Tosh-polimer kompozit (SPC) va klassik HDF asosidagi laminatni chuqur amaliy taqqoslash. Turli ekspluatatsiya sharoitlari uchun, byudjet, chidamlilik va yashirin risklarni hisobga olgan holda nimani tanlashni tahlil qilamiz.',
            takeaways: [
              'SPC — 100% suvga chidamlilik, laminat — cheklovlar bilan namlikka chidamlilik.',
              'Yuqori tirbandlikka ega tijoriy joylar uchun 43 klassdagi SPC — oltin standart.',
              'Quruq turar-joy zonalarda yotqizishda laminat byudjetda g‘alaba qozonadi.',
              'SPC mukammal tekis poydevorni talab qiladi (2 mm / 2 m gacha notekislik).',
              'Har ikkala material ham isitiladigan pol bilan mos keladi, lekin SPC issiqlikni samaraliroq o‘tkazadi.',
              'Qisqarish xavfi bo‘lgan yangi uylarda SPC qattiq yadrosi tufayli yanada barqaror.'
            ],
            sections: [],
            faq: []
          },
          en: {
            title: 'SPC OR LAMINATE: WHAT TO CHOOSE FOR HOME AND BUSINESS',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'A deep practical comparison of stone-polymer composite (SPC) and classic HDF-based laminate. We analyze what to choose for different operating conditions, considering budget, durability, and hidden risks.',
            takeaways: [
              'SPC - 100% waterproof, laminate - water-resistant with limitations.',
              'For high-traffic commercial spaces, SPC class 43 is the gold standard.',
              'Laminate wins on budget for installation in dry residential areas.',
              'SPC requires a perfectly level subfloor (up to 2mm/2m deviation).',
              'Both materials are compatible with underfloor heating, but SPC conducts heat more efficiently.',
              'In new buildings with settlement risk, SPC is more stable due to its rigid core.'
            ],
            sections: [],
            faq: []
          }
        }
      },
      {
        id: 'klass-tolshina-zashchitnyy-sloy',
        slug: 'klass-tolshina-zashchitnyy-sloy',
        image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000',
        translations: {
          ru: {
            title: 'Класс износостойкости SPC: 33/34/43, толщина, защитный слой',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Технические параметры, определяющие долговечность вашего пола. Разбираемся в маркировках и цифрах.',
            takeaways: [
              '43 класс — максимальная защита для бизнеса.',
              'Оптимальная толщина для дома — 4-5 мм.',
              'Защитный слой 0.5 мм — стандарт для коммерции.'
            ],
            sections: [
              {
                title: 'Классификация износа',
                content: 'Для дома достаточно 33-34 класса, для коммерческих зон с высокой нагрузкой выбирайте 43 класс.'
              }
            ],
            faq: []
          },
          uz: {
            title: 'SPC YEYILISH SINFI: 33/34/43, QALINLIK, HIMOYA QATLAMI',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Polingizning chidamliligini aniqlaydigan texnik parametrlar. Markirovkalarni va raqamlarni tahlil qilamiz.',
            takeaways: [
              '43 klass — biznes uchun maksimal himoya.',
              'Uy uchun optimal qalinlik — 4-5 mm.',
              '0,5 mm himoya qatlami — tijoriy maqsadlar uchun standart.'
            ],
            sections: [
              {
                title: 'Chidamlilik tasnifi',
                content: 'Uy uchun 33-34 klass, yuqori yuklamali tijoriy zonalari uchun 43 klass yetarli.'
              }
            ],
            faq: []
          },
          en: {
            title: 'SPC WEAR CLASS: 33/34/43, THICKNESS, WEAR LAYER',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Technical parameters that determine the durability of your floor. We break down the markings and numbers.',
            takeaways: [
              'Class 43 - maximum protection for business.',
              'Optimal thickness for home - 4-5 mm.',
              '0.5 mm wear layer - standard for commercial use.'
            ],
            sections: [
              {
                title: 'Wear Classification',
                content: 'Class 33-34 is sufficient for homes; choose class 43 for high-traffic commercial areas.'
              }
            ],
            faq: []
          }
        }
      },
      {
        id: 'teplyy-pol-vannaya',
        slug: 'teplyy-pol-vannaya',
        image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000',
        translations: {
          ru: {
            title: 'SPC для тёплого пола и ванной: что важно учесть',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Рекомендации по эксплуатации SPC в самых сложных условиях: при нагреве и прямом контакте с водой.',
            takeaways: [
              'SPC выдерживает нагрев до 28-30 градусов.',
              'Полностью заменяет плитку в ванной.',
              'Обладает высокой теплопроводностью.'
            ],
            sections: [
              {
                title: 'Температурный режим',
                content: 'SPC идеально проводит тепло. Рекомендуемая температура нагрева поверхности — до 28°C.'
              }
            ],
            faq: []
          },
          uz: {
            title: 'ISSIQ POL VA VANNAXONA UCHUN SPC: NIMALARGA E’TIBOR BERISH KERAK',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'SPCni eng qiyin sharoitlarda ekspluatatsiya qilish bo‘yicha tavsiyalar: qizdirish va suv bilan to‘g‘ridan-to‘g‘ri aloqada.',
            takeaways: [
              'SPC 28-30 darajagacha qizishga chidaydi.',
              'Vannaxonada plitkani to‘liq almashtiradi.',
              'Yuqori issiqlik o‘tkazuvchanligiga ega.'
            ],
            sections: [
              {
                title: 'Harorat rejimi',
                content: 'SPC issiqlikni mukammal o‘tkazadi. Yuzaning tavsiya etilgan harorati — 28°C gacha.'
              }
            ],
            faq: []
          },
          en: {
            title: 'SPC FOR HEATED FLOORS AND BATHROOMS: WHAT MATTERS',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Recommendations for using SPC in the most challenging conditions: with heating and direct water contact.',
            takeaways: [
              'SPC can withstand heating up to 28-30 degrees Celsius.',
              'Completely replaces tiles in bathrooms.',
              'Has high thermal conductivity.'
            ],
            sections: [
              {
                title: 'Temperature Regime',
                content: 'SPC conducts heat ideally. The recommended surface temperature is up to 28°C.'
              }
            ],
            faq: []
          }
        }
      },
      {
        id: 'podlozhka',
        slug: 'podlozhka',
        image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000',
        translations: {
          ru: {
            title: 'Подложка под SPC: нужна ли и как выбрать',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Почему обычная подложка не подходит для SPC и какую роль она играет в защите замковых соединений.',
            takeaways: [
              'Нужна только специальная плотная подложка.',
              'Толщина не должна превышать 1.5 мм.',
              'Встроенная подложка IXPE — лучший вариант.'
            ],
            sections: [
              {
                title: 'Требования к плотности',
                content: 'Используйте только специализированную подложку высокой плотности толщиной не более 1.5 мм.'
              }
            ],
            faq: []
          },
          uz: {
            title: 'SPC UCHUN TAGLIK: KERAKMI VA QANDAY TANLASH',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Nima uchun oddiy taglik SPCga mos kelmaydi va u qulf birikmalarini himoya qilishda qanday rol o‘ynaydi.',
            takeaways: [
              'Faqat maxsus zich taglik kerak.',
              'Qalinligi 1,5 mm dan oshmasligi kerak.',
              'O‘rnatilgan IXPE tagligi — eng yaxshi variant.'
            ],
            sections: [
              {
                title: 'Zichlik talablari',
                content: 'Faqat yuqori zichlikdagi, qalinligi 1,5 mm dan oshmaydigan maxsus taglikdan foydalaning.'
              }
            ],
            faq: []
          },
          en: {
            title: 'UNDERLAY FOR SPC: IS IT NEEDED AND HOW TO CHOOSE',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Why regular underlay is not suitable for SPC and what role it plays in protecting click joints.',
            takeaways: [
              'Only a special dense underlay is needed.',
              'Thickness should not exceed 1.5 mm.',
              'Integrated IXPE underlay is the best option.'
            ],
            sections: [
              {
                title: 'Density Requirements',
                content: 'Use only specialized high-density underlay with a thickness not exceeding 1.5 mm.'
              }
            ],
            faq: []
          }
        }
      },
      {
        id: 'spc-caryapiny-pyatna',
        slug: 'spc-caryapiny-pyatna',
        image: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?q=80&w=1000',
        translations: {
          ru: {
            title: 'Царапины и пятна на SPC: профилактика и решения',
            subtitle: 'БАЗА ЗНАНИЙ',
            seoTitle: 'Как убрать царапины и пятна на SPC покрытии: профилактика и решения',
            seoDescription: 'Практическое руководство по удалению пятен, следов резины и царапин с SPC пола. Безопасные методы чистки, профилактика повреждений, ремонт глубоких царапин.',
            lead: 'Практическое руководство по удалению царапин и пятен с SPC покрытия. Безопасные методы для разных типов загрязнений, профилактика повреждений и решения для восстановления внешнего вида пола.',
            takeaways: [
              '90% следов — это песок и неправильный инвентарь',
              'Пятна убираются точечно, без агрессивной химии',
              'Резина/металл дают самые «упрямые» следы — есть безопасный алгоритм',
              'Профилактика = коврик у входа + фетр + правильная швабра',
              'Глубокие царапины: маскировка/замена планки — без экспериментов',
              'Всегда тестируй средство на незаметном участке'
            ],
            sections: [],
            faq: []
          },
          uz: {
            title: 'SPCDA TIRNALISH VA DOG‘LAR: OLDINI OLISH VA YECHIMLAR',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'SPC qoplamasidagi tirnalishlar va dog‘larni olib tashlash bo‘yicha amaliy qo‘llanma. Har xil turdagi ifloslanishlar uchun xavfsiz usullar, shikastlanishlarning oldini olish va pol ko‘rinishini tiklash yechimlari.',
            takeaways: [
              'Izlarning 90% — bu qum va noto‘g‘ri inventar.',
              'Dog‘lar to‘g‘ridan-to‘g‘ri, agressiv kimyoviy moddalarsiz olinadi.',
              'Rezina/metall eng "qaynatilgan" izlarni beradi — xavfsiz algoritm mavjud.',
              'Oldini olish = kirishdagi gilamcha + filts + to‘g‘ri mop.',
              'Chuqur tirnalishlar: niqoblash/plankani almashtirish — eksperimentsiz.',
              'Har doim vositani ko‘zga ko‘rinmaydigan joyda sinab ko‘ring.'
            ],
            sections: [],
            faq: []
          },
          en: {
            title: 'SCRATCHES AND STAINS ON SPC: PREVENTION AND FIXES',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'A practical guide to removing scratches and stains from SPC flooring. Safe cleaning methods for various types of contamination, damage prevention, and solutions for restoring the floor\'s appearance.',
            takeaways: [
              '90% of marks are caused by sand and improper tools.',
              'Stains are removed locally, without aggressive chemicals.',
              'Rubber/metal leave the most "stubborn" marks - there\'s a safe algorithm.',
              'Prevention = doormat + felt pads + correct mop.',
              'Deep scratches: masking/plank replacement - no experiments.',
              'Always test the product in an inconspicuous area.'
            ],
            sections: [],
            faq: []
          }
        }
      }
    ]
  },
  {
    id: 'spc-paneli',
    slug: 'spc-paneli',
    title: 'SPC Панели',
    description: 'SPC стеновые панели SARGO — применение и монтаж. Влагостойкие панели для интерьеров: преимущества, монтаж, уход.',
    translations: {
      ru: {
        title: 'SPC ПАНЕЛИ',
        description: 'SPC стеновые панели SARGO — применение и монтаж. Влагостойкие панели для интерьеров: преимущества, монтаж, уход.'
      },
      uz: {
        title: 'SPC PANELI',
        description: 'SARGO SPC devor panellari — qo‘llash va o‘rnatish. Interyerlar uchun suvga chidamli panellar: afzalliklari, montaj, parvarishlash.'
      },
      en: {
        title: 'SPC PANELS',
        description: 'SARGO SPC wall panels — application and installation. Waterproof panels for interiors: benefits, installation, care.'
      }
    },
    seoTitle: 'Настенные SPC панели SARGO: каталог, применение, монтаж',
    seoDescription: 'Все о стеновых панелях SPC. Почему они лучше ПВХ и плитки, как монтировать в ванной и на кухне, правила ухода.',
    image: 'https://images.unsplash.com/photo-1517646281634-aae05d4434b2?q=80&w=1000',
    articles: [
      {
        id: 'chto-eto',
        slug: 'chto-eto',
        image: 'https://images.unsplash.com/photo-1517646281634-aae05d4434b2?q=80&w=1000',
        translations: {
          ru: {
            title: 'SPC стеновые панели: что это и почему их выбирают',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Минерально-полимерные панели для стен — современная альтернатива кафелю и краске во влажных зонах.',
            takeaways: [
              'SPC панели — это влагостойкая альтернатива кафелю.',
              'Обладают высокой ударопрочностью по сравнению с ПВХ.',
              'Монтируются на клей или обрешетку.',
              'Имеют реалистичные текстуры камня и дерева.',
              'Не требуют затирки швов как плитка.'
            ],
            sections: [
              {
                title: 'Инновации в отделке стен',
                content: 'Панели обладают той же жесткостью и водостойкостью, что и SPC ламинат, но адаптированы для вертикального монтажа. Это решение позволяет сократить время отделки ванной комнаты в 3-4 раза.'
              },
              {
                title: 'Преимущества перед кафелем',
                content: 'В отличие от плитки, SPC панели не трескаются при усадке здания, теплы на ощупь и не имеют швов, в которых могла бы завестись плесень.'
              }
            ],
            faq: [
              { question: 'Нужно ли выравнивать стены?', answer: 'При монтаже на клей — да, при монтаже на обрешетку — нет.' }
            ]
          },
          uz: {
            title: 'SPC DEVOR PANELLARI: BU NIMA VA NEGA TANLANADI',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Devorlar uchun mineral-polimer panellar — nam zonalarda kafel va bo\'yoqqa zamonaviy muqobil.',
            takeaways: [
              'SPC panellari — kafelga suvga chidamli muqobil.',
              'PVX bilan solishtirganda yuqori zarbga chidamlilikka ega.',
              'Yelim yoki qolipga o‘rnatiladi.',
              'Tosh va yog‘ochning realistik teksturalariga ega.',
              'Plitka kabi bo‘g‘inlarni tozalashni talab qilmaydi.'
            ],
            sections: [
              {
                title: 'Devorlarni bezashdagi yangiliklar',
                content: 'Panellar SPC laminati kabi qattiqlik va suvga chidamlilikka ega, lekin vertikal o‘rnatish uchun moslashtirilgan. Bu yechim vannaxonani bezash vaqtini 3-4 barobar qisqartirish imkonini beradi.'
              },
              {
                title: 'Kafelga nisbatan afzalliklari',
                content: 'Plitkadan farqli o‘laroq, SPC panellari bino qisqarishida yorilmaydi, qo‘lda issiq va mog‘or yig‘ilishi mumkin bo‘lgan bo‘g‘inlarga ega emas.'
              }
            ],
            faq: [
              { question: 'Devorlarni tekislash kerakmi?', answer: 'Yelim bilan o‘rnatishda — ha, qolipga o‘rnatishda — yo‘q.' }
            ]
          },
          en: {
            title: 'SPC WALL PANELS: WHAT THEY ARE AND WHY PEOPLE CHOOSE THEM',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Mineral-polymer wall panels are a modern alternative to tiles and paint in wet areas.',
            takeaways: [
              'SPC panels are a waterproof alternative to tiles.',
              'They have high impact resistance compared to PVC.',
              'They are installed with adhesive or on a frame.',
              'They feature realistic stone and wood textures.',
              'They do not require grout like tiles.'
            ],
            sections: [
              {
                title: 'Innovations in Wall Finishing',
                content: 'The panels possess the same rigidity and water resistance as SPC flooring but are adapted for vertical installation. This solution can reduce bathroom finishing time by 3-4 times.'
              },
              {
                title: 'Advantages Over Tiles',
                content: 'Unlike tiles, SPC panels do not crack due to building settlement, are warm to the touch, and have no seams where mold could form.'
              }
            ],
            faq: [
              { question: 'Do walls need to be leveled?', answer: 'Yes, for adhesive installation; no, for frame installation.' }
            ]
          }
        }
      },
      {
        id: 'kupit-cena',
        slug: 'kupit-cena',
        image: 'https://images.unsplash.com/photo-1517646281634-aae05d4434b2?q=80&w=1000',
        translations: {
          ru: {
            title: 'Купить SPC панели: цена и как выбрать',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Как не ошибиться при покупке панелей и на что обратить внимание при расчете бюджета.',
            takeaways: [
              'Размер листа, тип стыковки (замковая или клеевая) и стойкость декоративного слоя.',
              'Стоимость зависит от размера, типа стыковки и износостойкости декоративного слоя.'
            ],
            sections: [
              {
                title: 'Критерии выбора',
                content: 'Размер листа, тип стыковки (замковая или клеевая) и стойкость декоративного слоя.'
              }
            ],
            faq: []
          },
          uz: {
            title: 'SPC PANELLARNI SOTIB OLISH: NARX VA QANDAY TANLASH',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Panellarni sotib olishda qanday xato qilmaslik va budjetni hisoblashda nimalarga e\'tibor berish kerak.',
            takeaways: [
              'List hajmi, biriktirish turi (qulfli yoki yelimli) va dekorativ qatlamning chidamliligi.',
              'Narx list hajmi, biriktirish turi va dekorativ qatlamning chidamliligiga bog‘liq.'
            ],
            sections: [
              {
                title: 'Tanlash mezonlari',
                content: 'List hajmi, biriktirish turi (qulfli yoki yelimli) va dekorativ qatlamning chidamliligi.'
              }
            ],
            faq: []
          },
          en: {
            title: 'BUY SPC PANELS: PRICE AND HOW TO CHOOSE',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'How to avoid mistakes when buying panels and what to look for when calculating the budget.',
            takeaways: [
              'Sheet size, joint type (click or glue), and decorative layer durability.',
              'The cost depends on the sheet size, joint type, and decorative layer durability.'
            ],
            sections: [
              {
                title: 'Selection Criteria',
                content: 'Sheet size, joint type (click or glue), and decorative layer durability.'
              }
            ],
            faq: []
          }
        }
      },
      {
        id: 'vannaya-kuhnya',
        slug: 'vannaya-kuhnya',
        image: 'https://images.unsplash.com/photo-1517646281634-aae05d4434b2?q=80&w=1000',
        translations: {
          ru: {
            title: 'SPC панели для ванной и кухни: можно ли и как правильно',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Использование панелей в зонах с прямым контактом воды. Секреты герметизации и долговечности.',
            takeaways: [
              'Панели SARGO на 100% водостойки и не деформируются от пара, что делает их идеальными для душевых зон.',
              'Идеальны для ванных комнат и кухонь благодаря 100% водостойкости.',
              'Не боятся пара и прямого контакта с водой.'
            ],
            sections: [
              {
                title: 'Влагозащита',
                content: 'Панели SARGO на 100% водостойки и не деформируются от пара, что делает их идеальными для душевых зон.'
              }
            ],
            faq: [
              { question: 'Нужно ли выравнивать стены?', answer: 'При монтаже на клей — да, при монтаже на обрешетку — нет.' }
            ]
          },
          uz: {
            title: 'VANNA VA OSHXONA UCHUN SPC PANELLAR: MUMKINMI VA QANDAY TO‘G‘RI',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Suv bilan to\'g\'ridan-to\'g\'ri aloqa qiladigan joylarda panellardan foydalanish. Germetizatsiya va uzoq umr ko\'rish sirlari.',
            takeaways: [
              'SARGO panellari 100% suvga chidamli va bug\'dan deformatsiyalanmaydi, bu ularni dush zonalari uchun ideal qiladi.',
              '100% suvga chidamliligi tufayli vannaxonalar va oshxonalar uchun ideal.',
              'Bug‘ va suv bilan to‘g‘ridan-to‘g‘ri aloqadan qo‘rqmaydi.'
            ],
            sections: [
              {
                title: 'Suvga chidamlilik',
                content: 'SARGO panellari 100% suvga chidamli va bug‘dan deformatsiyalanmaydi, bu ularni dush zonalari uchun ideal qiladi.'
              }
            ],
            faq: [
              { question: 'Devorlarni tekislash kerakmi?', answer: 'Yelim bilan o‘rnatishda — ha, qolipga o‘rnatishda — yo‘q.' }
            ]
          },
          en: {
            title: 'SPC PANELS FOR BATHROOMS AND KITCHENS: CAN YOU USE THEM AND HOW',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Using panels in areas with direct water contact. Secrets of sealing and durability.',
            takeaways: [
              'SARGO panels are 100% waterproof and do not deform from steam, making them ideal for shower areas.',
              'Ideal for bathrooms and kitchens due to 100% water resistance.',
              'They are not afraid of steam and direct contact with water.'
            ],
            sections: [
              {
                title: 'Water Resistance',
                content: 'SARGO panels are 100% waterproof and do not deform from steam, making them ideal for shower areas.'
              }
            ],
            faq: [
              { question: 'Do walls need to be leveled?', answer: 'Yes, for adhesive installation; no, for frame installation.' }
            ]
          }
        }
      },
      {
        id: 'razmery-styki-ugly',
        slug: 'razmery-styki-ugly',
        image: 'https://images.unsplash.com/photo-1517646281634-aae05d4434b2?q=80&w=1000',
        translations: {
          ru: {
            title: 'Размеры и стыки SPC панелей: углы, примыкания, аккуратный монтаж',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Как добиться безупречного вида отделки: секреты оформления внешних и внутренних углов.',
            takeaways: [
              'Использование декоративных профилей или стыковка встык с герметиком — выбираем лучший вариант.',
              'Аккуратное оформление углов и примыканий — залог эстетичного вида.'
            ],
            sections: [
              {
                title: 'Оформление стыков',
                content: 'Использование декоративных профилей или стыковка встык с герметиком — выбираем лучший вариант.'
              }
            ],
            faq: [
              { question: 'Нужно ли выравнивать стены?', answer: 'При монтаже на клей — да, при монтаже на обрешетку — нет.' }
            ]
          },
          uz: {
            title: 'SPC PANELLAR O‘LCHAMI VA ULASH JOYLARI: BURCHAKLAR, TUTASHUVLAR, TOZA MONTAJ',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Qanday qilib mukammal ko\'rinishga erishish mumkin: tashqi va ichki burchaklarni bezash sirlari.',
            takeaways: [
              'Dekorativ profillardan foydalanish yoki yelim bilan yopishtirish — eng yaxshi variantni tanlaymiz.',
              'Burchaklar va tutashuvlarni toza bezash — estetik ko\'rinishning kaliti.'
            ],
            sections: [
              {
                title: 'Ulash joylarini bezash',
                content: 'Dekorativ profillardan foydalanish yoki yelim bilan yopishtirish — eng yaxshi variantni tanlaymiz.'
              }
            ],
            faq: [
              { question: 'Devorlarni tekislash kerakmi?', answer: 'Yelim bilan o‘rnatishda — ha, qolipga o‘rnatishda — yo‘q.' }
            ]
          },
          en: {
            title: 'SPC PANEL SIZES AND JOINTS: CORNERS, CONNECTIONS, NEAT INSTALLATION',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'How to achieve a flawless finish: secrets of finishing external and internal corners.',
            takeaways: [
              'Using decorative profiles or butt-jointing with sealant - we choose the best option.',
              'Neat finishing of corners and junctions is key to an aesthetic appearance.'
            ],
            sections: [
              {
                title: 'Joint Finishing',
                content: 'Using decorative profiles or butt-jointing with sealant - we choose the best option.'
              }
            ],
            faq: [
              { question: 'Do walls need to be leveled?', answer: 'Yes, for adhesive installation; no, for frame installation.' }
            ]
          }
        }
      },
      {
        id: 'spc-vs-pvh',
        slug: 'spc-vs-pvh',
        image: 'https://images.unsplash.com/photo-1517646281634-aae05d4434b2?q=80&w=1000',
        translations: {
          ru: {
            title: 'SPC панели или ПВХ панели: что лучше и почему',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Сравнение прочности, экологичности и внешнего вида эконом-сегмента ПВХ и премиального SPC.',
            takeaways: [
              'В отличие от пустотелых ПВХ панелей, SPC — это полнотелый, ударопрочный материал, который не боится вмятин.',
              'SPC прочнее и долговечнее ПВХ панелей.',
              'Экологичность SPC выше благодаря отсутствию вредных добавок.'
            ],
            sections: [
              {
                title: 'Прочность и долговечность',
                content: 'В отличие от пустотелых ПВХ панелей, SPC — это полнотелый, ударопрочный материал, который не боится вмятин.'
              }
            ],
            faq: [
              { question: 'Нужно ли выравнивать стены?', answer: 'При монтаже на клей — да, при монтаже на обрешетку — нет.' }
            ]
          },
          uz: {
            title: 'SPC YOKI PVX PANELLAR: QAYSI YAXSHIROQ VA NEGA',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'PVX iqtisodiy segmenti va premium SPCning mustahkamligi, ekologik tozaligi va tashqi ko\'rinishini taqqoslash.',
            takeaways: [
              'Bo‘shliqli PVX panellaridan farqli o‘laroq, SPC — to‘liq, zarbga chidamli material bo‘lib, u deformatsiyadan qo‘rqmaydi.',
              'SPC PVX panellaridan ko‘ra mustahkam va chidamli.',
              'Zararli qo‘shimchalarning yo‘qligi tufayli SPCning ekologik tozaligi yuqori.'
            ],
            sections: [
              {
                title: 'Mustahkamlik va chidamlilik',
                content: 'Bo‘shliqli PVX panellaridan farqli o‘laroq, SPC — to‘liq, zarbga chidamli material bo‘lib, u deformatsiyadan qo‘rqmaydi.'
              }
            ],
            faq: [
              { question: 'Devorlarni tekislash kerakmi?', answer: 'Yelim bilan o‘rnatishda — ha, qolipga o‘rnatishda — yo‘q.' }
            ]
          },
          en: {
            title: 'SPC PANELS VS PVC PANELS: WHAT’S BETTER AND WHY',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Comparison of durability, eco-friendliness, and appearance of budget PVC and premium SPC.',
            takeaways: [
              'Unlike hollow PVC panels, SPC is a solid, impact-resistant material that is not afraid of dents.',
              'SPC is stronger and more durable than PVC panels.',
              'SPC is more environmentally friendly due to the absence of harmful additives.'
            ],
            sections: [
              {
                title: 'Strength and Durability',
                content: 'Unlike hollow PVC panels, SPC is a solid, impact-resistant material that is not afraid of dents.'
              }
            ],
            faq: [
              { question: 'Do walls need to be leveled?', answer: 'Yes, for adhesive installation; no, for frame installation.' }
            ]
          }
        }
      },
      {
        id: 'paneli-chistka',
        slug: 'paneli-chistka',
        image: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?q=80&w=1000',
        translations: {
          ru: {
            title: 'Уход за SPC панелями: чем чистить и чего избегать',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Поддержание безупречного вида стен в ванной и на кухне. Удаление жира и известкового налета.',
            takeaways: [
              'Гладкая поверхность панелей не впитывает грязь, поэтому для уборки достаточно мягкой губки.',
              'Легко чистятся от жира и известкового налета.',
              'Не требуют агрессивных чистящих средств.'
            ],
            sections: [
              {
                title: 'Чистка стен',
                content: 'Гладкая поверхность панелей не впитывает грязь, поэтому для уборки достаточно мягкой губки.'
              }
            ],
            faq: [
              { question: 'Можно ли использовать абразивные чистящие средства?', answer: 'Нет, это может повредить защитный слой.' },
              { question: 'Как удалить стойкие пятна?', answer: 'Используйте мягкое моющее средство и теплую воду. При необходимости можно применить изопропиловый спирт.' }
            ]
          },
          uz: {
            title: 'SPC PANELLAR PARVARISHI: NIMA BILAN TOZALASH VA NIMADAN SAQLANISH',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Hammom va oshxonadagi devorlarning mukammal ko\'rinishini saqlash. Yog\' va ohak qatlamlarini tozalash.',
            takeaways: [
              'Panellarning silliq yuzasi kirni shimib olmaydi, shuning uchun tozalash uchun yumshoq gubka yetarli.',
              'Yog\' va ohak qatlamlaridan osongina tozalanadi.',
              'Agressiv tozalash vositalarini talab qilmaydi.'
            ],
            sections: [
              {
                title: 'Devorlarni tozalash',
                content: 'Panellarning silliq yuzasi kirni shimib olmaydi, shuning uchun tozalash uchun yumshoq gubka yetarli.'
              }
            ],
            faq: [
              { question: 'Aktiv tozalash vositalaridan foydalanish mumkinmi?', answer: 'Yo‘q, bu himoya qatlamiga zarar yetkazishi mumkin.' },
              { question: 'Qaynatilgan dog‘larni qanday olib tashlash mumkin?', answer: 'Yumshoq yuvish vositasi va iliq suvdan foydalaning. Agar kerak bo‘lsa, izopropil spirtidan foydalanish mumkin.' }
            ]
          },
          en: {
            title: 'SPC PANEL CARE: WHAT TO CLEAN WITH AND WHAT TO AVOID',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Maintaining a flawless wall appearance in bathrooms and kitchens. Removing grease and limescale.',
            takeaways: [
              'The smooth surface of the panels does not absorb dirt, so a soft sponge is sufficient for cleaning.',
              'Easily cleaned of grease and limescale.',
              'Does not require aggressive cleaning agents.'
            ],
            sections: [
              {
                title: 'Wall Cleaning',
                content: 'The smooth surface of the panels does not absorb dirt, so a soft sponge is sufficient for cleaning.'
              }
            ],
            faq: [
              { question: 'Can abrasive cleaning agents be used?', answer: 'No, it can damage the protective layer.' },
              { question: 'How to remove stubborn stains?', answer: 'Use a mild detergent and warm water. Isopropyl alcohol can be used if necessary.' }
            ]
          }
        }
      }
    ]
  },
  {
    id: 'carpet-tile',
    slug: 'carpet-tile',
    title: 'Ковровая плитка',
    description: 'Ковровая плитка SARGO — выбор, укладка, уход. Коммерческие и интерьерные решения: износостойкость, акустика, сервис.',
    translations: {
      ru: {
        title: 'КОВРОВАЯ ПЛИТКА',
        description: 'Ковровая плитка SARGO — выбор, укладка, уход. Коммерческие и интерьерные решения: износостойкость, акустика, сервис.'
      },
      uz: {
        title: 'GILAM PLITKA',
        description: 'SARGO gilam plitkalari — tanlash, yotqizish, parvarishlash. Tijorat va interyer yechimlari: chidamlilik, akustika, xizmat ko‘rsatish.'
      },
      en: {
        title: 'CARPET TILE',
        description: 'SARGO carpet tile — selection, installation, care. Commercial and interior solutions: durability, acoustics, service.'
      }
    },
    seoTitle: 'Ковровая плитка SARGO: модульное ковровое покрытие для офиса',
    seoDescription: 'Преимущества ковровой плитки. Почему она лучше рулонного ковролина, как укладывать в офисе, советы по уходу и замене.',
    image: 'https://images.unsplash.com/photo-1581850518616-bcb8186c443e?q=80&w=1000',
    articles: [
      {
        id: 'chto-eto',
        slug: 'chto-eto',
        image: 'https://images.unsplash.com/photo-1581850518616-bcb8186c443e?q=80&w=1000',
        translations: {
          ru: {
            title: 'Ковровая плитка: что это и когда она выгоднее рулона',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Преимущества модульного формата: от легкости транспортировки до минимизации отходов при укладке.',
            takeaways: [
              'Модульность позволяет менять только поврежденные участки.',
              'Экономия материала на обрезках до 15%.',
              'Удобство транспортировки в обычных коробках.',
              'Отличная акустическая изоляция для офисов.',
              'Долгий срок службы в условиях высокой проходимости.'
            ],
            sections: [
              {
                title: 'Экономия и удобство',
                content: 'При укладке плитки процент обрезков составляет 3-5%, тогда как у рулона он может достигать 20%. Это делает ковровую плитку идеальным решением для помещений со сложной геометрией.'
              },
              {
                title: 'Практичность эксплуатации',
                content: 'В случае сильного загрязнения или износа отдельного участка, вам не нужно менять все покрытие — достаточно заменить одну плитку.'
              }
            ],
            faq: [
              { question: 'Можно ли укладывать на старый линолеум?', answer: 'Да, если он ровный и хорошо приклеен к основанию.' }
            ]
          },
          uz: {
            title: 'GILAM PLITKA: BU NIMA VA QACHON RULONDAN FOYDALI',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Modulli formatning afzalliklari: tashish qulayligidan tortib, yotqizishda chiqindilarni kamaytirishgacha.',
            takeaways: [
              'Modullik faqat shikastlangan joylarni almashtirish imkonini beradi.',
              'Chiqindilarda materialni 15% gacha tejash.',
              'Oddiy qutilarda tashish qulayligi.',
              'Ofislar uchun ajoyib akustik izolyatsiya.',
              'Yuqori o‘tish joylarida uzoq xizmat qilish muddati.'
            ],
            sections: [
              {
                title: 'Tejalish va qulaylik',
                content: 'Plitkani yotqizishda chiqindilar foizi 3-5% ni tashkil qiladi, rulonda esa 20% gacha yetishi mumkin. Bu gilam plitkani murakkab geometriyali xonalar uchun ideal yechimga aylantiradi.'
              },
              {
                title: 'Foydalanishning amaliyligi',
                content: 'Agar alohida joy kuchli ifloslansa yoki eskirsa, siz butun qoplamani almashtirishingiz shart emas — bitta plitkani almashtirish kifoya.'
              }
            ],
            faq: [
              { question: 'Eski linoleum ustiga yotqizish mumkinmi?', answer: 'Ha, agar u tekis va poydevorga yaxshi yopishtirilgan bo‘lsa.' }
            ]
          },
          en: {
            title: 'CARPET TILES: WHAT THEY ARE AND WHEN THEY’RE BETTER THAN ROLLS',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Advantages of the modular format: from ease of transportation to minimizing waste during installation.',
            takeaways: [
              'Modularity allows replacing only damaged sections.',
              'Material savings on offcuts up to 15%.',
              'Convenience of transportation in standard boxes.',
              'Excellent acoustic insulation for offices.',
              'Long service life in high-traffic conditions.'
            ],
            sections: [
              {
                title: 'Savings and Convenience',
                content: 'When installing tiles, the percentage of offcuts is 3-5%, whereas for rolls it can reach 20%. This makes carpet tiles an ideal solution for rooms with complex geometry.'
              },
              {
                title: 'Practicality of Use',
                content: 'In case of severe soiling or wear of a specific area, you don\'t need to replace the entire covering – just replace one tile.'
              }
            ],
            faq: [
              { question: 'Can it be laid on old linoleum?', answer: 'Yes, if it is level and well-adhered to the subfloor.' }
            ]
          }
        }
      },
      {
        id: 'kupit-cena-office',
        slug: 'kupit-cena-office',
        image: 'https://images.unsplash.com/photo-1581850518616-bcb8186c443e?q=80&w=1000',
        translations: {
          ru: {
            title: 'Купить ковровую плитку: цена и выбор для офиса',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Как подобрать оптимальный вариант для коммерческого пространства с учетом проходимости.',
            takeaways: [
              'Для офисов класса А и Б рекомендуется использовать плитку с нейлоновым ворсом 33 класса износостойкости.',
              'Выбирайте плитку 33 класса для офисов класса А и Б.',
              'Нейлоновый ворс обеспечивает долговечность и простоту ухода.'
            ],
            sections: [
              {
                title: 'Классы для бизнеса',
                content: 'Для офисов класса А и Б рекомендуется использовать плитку с нейлоновым ворсом 33 класса износостойкости.'
              }
            ],
            faq: [
              { question: 'Можно ли укладывать на старый линолеум?', answer: 'Да, если он ровный и хорошо приклеен к основанию.' }
            ]
          },
          uz: {
            title: 'GILAM PLITKANI SOTIB OLISH: NARX VA OFIS UCHUN TANLOV',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Tijorat maydoni uchun o‘tish darajasini hisobga olgan holda optimal variantni qanday tanlash kerak.',
            takeaways: [
              'A va B sinfidagi ofislar uchun 33-sinf neylon tukli plitkani ishlatish tavsiya etiladi.',
              'A va B sinfidagi ofislar uchun 33-sinf plitkani tanlang.',
              'Neylon tuk chidamlilik va parvarishlash qulayligini ta\'minlaydi.'
            ],
            sections: [
              {
                title: 'Biznes uchun sinflar',
                content: 'A va B sinfidagi ofislar uchun 33-sinf neylon tukli plitkani ishlatish tavsiya etiladi.'
              }
            ],
            faq: [
              { question: 'Eski linoleum ustiga yotqizish mumkinmi?', answer: 'Ha, agar u tekis va poydevorga yaxshi yopishtirilgan bo‘lsa.' }
            ]
          },
          en: {
            title: 'BUY CARPET TILES: PRICE AND OFFICE SELECTION',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'How to choose the optimal option for commercial space considering foot traffic.',
            takeaways: [
              'For Class A and B offices, it is recommended to use tiles with nylon pile of wear resistance class 33.',
              'Choose class 33 tiles for Class A and B offices.',
              'Nylon pile ensures durability and ease of maintenance.'
            ],
            sections: [
              {
                title: 'Classes for Business',
                content: 'For Class A and B offices, it is recommended to use tiles with nylon pile of wear resistance class 33.'
              }
            ],
            faq: [
              { question: 'Can it be laid on old linoleum?', answer: 'Yes, if it is level and well-adhered to the subfloor.' }
            ]
          }
        }
      },
      {
        id: 'kommercheskaya-iznos',
        slug: 'kommercheskaya-iznos',
        image: 'https://images.unsplash.com/photo-1581850518616-bcb8186c443e?q=80&w=1000',
        translations: {
          ru: {
            title: 'Коммерческая ковровая плитка: износостойкость и практичность',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Разбираемся в составах ворса и основы, которые делают плитку устойчивой к колесам кресел и обуви.',
            takeaways: [
              'Полиамид (нейлон) — эталон для коммерции. Он не приминается и легко очищается от загрязнений.',
              'Нейлоновый ворс устойчив к истиранию и легко чистится.',
              'Плотная основа обеспечивает стабильность размеров.'
            ],
            sections: [
              {
                title: 'Материалы ворса',
                content: 'Полиамид (нейлон) — эталон для коммерции. Он не приминается и легко очищается от загрязнений.'
              }
            ],
            faq: [
              { question: 'Можно ли укладывать на старый линолеум?', answer: 'Да, если он ровный и хорошо приклеен к основанию.' }
            ]
          },
          uz: {
            title: 'TIJORAT GILAM PLITKASI: CHIDAMLILIK VA AMALIYLIK',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Plitkani kreslo g‘ildiraklari va poyabzallarga chidamli qiladigan tuk va asos tarkibini tahlil qilamiz.',
            takeaways: [
              'Poliamid (neylon) — tijorat uchun standart. U bosilmaydi va iflosliklardan osongina tozalanadi.',
              'Neylon tuk ishqalanishga chidamli va osongina tozalanadi.',
              'Zich asos o‘lchovlarning barqarorligini ta\'minlaydi.'
            ],
            sections: [
              {
                title: 'Tuk materiallari',
                content: 'Poliamid (neylon) — tijorat uchun standart. U bosilmaydi va iflosliklardan osongina tozalanadi.'
              }
            ],
            faq: [
              { question: 'Eski linoleum ustiga yotqizish mumkinmi?', answer: 'Ha, agar u tekis va poydevorga yaxshi yopishtirilgan bo‘lsa.' }
            ]
          },
          en: {
            title: 'COMMERCIAL CARPET TILES: DURABILITY AND PRACTICALITY',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'Understanding the pile and backing compositions that make tiles resistant to chair wheels and footwear.',
            takeaways: [
              'Polyamide (nylon) is the standard for commercial use. It does not crush and is easily cleaned of dirt.',
              'Nylon pile is resistant to abrasion and easy to clean.',
              'A dense backing ensures dimensional stability.'
            ],
            sections: [
              {
                title: 'Pile Materials',
                content: 'Polyamide (nylon) is the standard for commercial use. It does not crush and is easily cleaned of dirt.'
              }
            ],
            faq: [
              { question: 'Can it be laid on old linoleum?', answer: 'Yes, if it is level and well-adhered to the subfloor.' }
            ]
          }
        }
      },
      {
        id: 'uhod-chistka',
        slug: 'uhod-chistka',
        image: 'https://images.unsplash.com/photo-1581850518616-bcb8186c443e?q=80&w=1000',
        translations: {
          ru: {
            title: 'Уход за ковровой плиткой: чистка и замена одной плитки',
            subtitle: 'БАЗА ЗНАНИЙ',
            lead: 'Как поддерживать чистоту в офисе и что делать, если на пол пролили кофе или краску.',
            takeaways: [
              'Главное преимущество — вы можете просто вынуть грязную плитку, промыть её или заменить на новую из запаса.',
              'Локальная замена экономит время и бюджет.',
              'Простая чистка пылесосом поддерживает чистоту.'
            ],
            sections: [
              {
                title: 'Локальная замена',
                content: 'Главное преимущество — вы можете просто вынуть грязную плитку, промыть её или заменить на новую из запаса.'
              }
            ],
            faq: [
              { question: 'Можно ли укладывать на старый линолеум?', answer: 'Да, если он ровный и хорошо приклеен к основанию.' }
            ]
          },
          uz: {
            title: 'GILAM PLITKA PARVARISHI: TOZALASH VA BIR PLITKANI ALMASHTIRISH',
            subtitle: 'BILIMLAR BAZASI',
            lead: 'Ofisda tozalikni qanday saqlash kerak va agar polga kofe yoki bo‘yoq to‘kilsa nima qilish kerak.',
            takeaways: [
              'Asosiy afzallik — siz kir plitkani olib tashlashingiz, yuvishingiz yoki zaxiradagi yangisi bilan almashtirishingiz mumkin.',
              'Mahalliy almashtirish vaqt va byudjetni tejaydi.',
              'Oddiy changyutkich bilan tozalash tozalikni ta\'minlaydi.'
            ],
            sections: [
              {
                title: 'Mahalliy almashtirish',
                content: 'Asosiy afzallik — siz kir plitkani olib tashlashingiz, yuvishingiz yoki zaxiradagi yangisi bilan almashtirishingiz mumkin.'
              }
            ],
            faq: [
              { question: 'Eski linoleum ustiga yotqizish mumkinmi?', answer: 'Ha, agar u tekis va poydevorga yaxshi yopishtirilgan bo‘lsa.' }
            ]
          },
          en: {
            title: 'CARPET TILE CARE: CLEANING AND REPLACING A SINGLE TILE',
            subtitle: 'KNOWLEDGE BASE',
            lead: 'How to maintain cleanliness in the office and what to do if coffee or paint is spilled on the floor.',
            takeaways: [
              'The main advantage is that you can simply remove a dirty tile, wash it, or replace it with a new one from stock.',
              'Local replacement saves time and budget.',
              'Simple vacuuming maintains cleanliness.'
            ],
            sections: [
              {
                title: 'Local Replacement',
                content: 'The main advantage is that you can simply remove a dirty tile, wash it, or replace it with a new one from stock.'
              }
            ],
            faq: [
              { question: 'Can it be laid on old linoleum?', answer: 'Yes, if it is level and well-adhered to the subfloor.' }
            ]
          }
        }
      }
    ]
  }
];
